import { withoutEntries } from "../promotions/entry";
import { entryKey } from "../promotions/identity";
import type { OfferKey } from "../promotions/identity";
import type { PartnerDirectory, PartnerPort, ProviderRef } from "../promotions/partners/port";
import type { ReleaseStep } from "../promotions/release";
import type { BillDiscount, Sale } from "../promotions/types";
import type { Toasts } from "../toast";
import type { SaleWriteIntent } from "./changes";
import { PROMOTION_EFFECTS, type CustomerLoyaltyFetchPlan } from "./promotions";
import { runSaleEffects } from "./registry";

/**
 * The executor of sale effects v2's promotion effects (docs/architecture/SALE_EFFECTS.md §6 E1b): it runs
 * the plans `runSaleEffects` returns over the partner port, the way v1's four listener effects did
 * (`loyaltyRefundEffect`, `dotpeOfferReleaseEffect`, `resetSaleEffect`, `customerLoyaltyFetchEffect`).
 *
 * Nothing is imported that touches a store, the network or a screen: the port, the directory, the sale, the
 * write path, the toast, the customer record's loader and its loaded check all come in as `deps`, so a
 * host (the write path, E3) wires one executor and a test wires fakes.
 *
 * What it keeps from v1, and why:
 * - **Offline runs nothing**: every step is server-backed (v1 returned early offline).
 * - **One refund at a time** (v1's `refundInFlight`): a second refund asked for while one is out is skipped.
 *   The same holds for one coupon's release and one customer's record load, which v1 got from writing the
 *   strip synchronously and which an executor that writes after the answer must guard itself.
 * - **A failed refund leaves the points line** (the backend still holds the points); a coupon that failed
 *   to release is still taken off (v1's DotPe effect stripped it anyway) and the failure is said.
 *
 * 🔴 **Each effect runs exactly once** (decision 32): this executor is wired only where v1's listener is not
 * attached, behind E3's gate. Wiring both would refund and release twice.
 */

export interface SaleEffectsExecutorDeps {
  port: Pick<PartnerPort, "refundPoints" | "release">;
  directory(): PartnerDirectory;
  /** The sale as it stands now: what a bill-change refund and a coupon removal act on. */
  currentSale(): Sale;
  /** The host's write path: prices and saves. Called at most once per plan. */
  write(sale: Sale): void;
  isOnline(): boolean;
  toast: Pick<Toasts, "show">;
  /**
   * Ask the customer domain to load one customer's detailed record (tier, rewards, `externalLoyaltyData`):
   * v1's `fetchDetailedCustomerInfo(customerRefId)`. Fire and forget; the host wires its loader at E3.
   * It is NOT the points balance, which is the loyalty panel's own read (decision 51).
   */
  loadCustomerRecord(customerRefId: string): void;
  /** Whether that customer's detailed record is already loaded (v1: `selectCustomerLoyaltyInfo` present and `selectCustomerLoyaltyRefId` equal). */
  isCustomerRecordLoaded(customerRefId: string): boolean;
}

/** v1's words (`loyaltyRefundEffect`, `loyaltyRefundHelper`) */
export const REFUND_DONE_MESSAGE = "Loyalty points removed successfully";
export const REFUND_FAILED_MESSAGE = "Failed to remove loyalty points";
/** What a coupon's failed release says when the answer carries no words (v1 said the backend's `errorCode`, when there was one). */
export const RELEASE_FAILED_MESSAGE = "Failed to remove the coupon";
/** v1's `removeExternalDiscount` words for a release it could not send */
const RELEASE_SKIPPED_PHONE_MESSAGE = "Customer phone number is missing";
const RELEASE_SKIPPED_OTHER_MESSAGE = "Something went wrong";

export type ExecutedStepKind = ReleaseStep["kind"] | "fetchLoyaltyRecord";
export type SkipReason = "offline" | "in-flight" | "no-provider" | "loaded";

/** What one run did, for the host's logging and the tests. */
export interface ExecutionSummary {
  /** Each step that called the port, with what came back (`refunded`, `released`, `requested`, …). */
  ran: { kind: ExecutedStepKind; result: string }[];
  skipped: { kind: ExecutedStepKind; reason: SkipReason }[];
  /** Sales handed to `write`. At most one per promotion-release plan. */
  writes: number;
}

export interface SaleEffectsExecutor {
  /**
   * Plan the write `previous` → `next` (told by `intent`) and run every plan. Resolves when every step has
   * finished. Call it from the write path right after a write, synchronously, so the in-flight guards are
   * armed before a second write can ask.
   */
  run(previous: Sale | null | undefined, next: Sale | null | undefined, intent?: SaleWriteIntent): Promise<ExecutionSummary>;
}

/** The partner whose points line this is: its `meta.discountCode` is the line's code (v1's `getLoyaltyProviderForDiscount`). */
function providerOfPointsLine(directory: PartnerDirectory, entry: BillDiscount): ProviderRef | undefined {
  const code = (entry as Record<string, unknown>).discountCode;
  return typeof code === "string" && code ? directory.providers.find((provider) => provider.meta?.discountCode === code) : undefined;
}

/** `sale` with each of `entries` taken off by identity (`withoutEntries`); one that has no key is taken off by reference. */
function takeOffEntries(sale: Sale, entries: readonly BillDiscount[]): Sale {
  const keys = entries.map(entryKey).filter((key): key is OfferKey => key !== null);
  const keyless = entries.filter((entry) => entryKey(entry) === null);
  const list = withoutEntries(sale.billDiscountList ?? [], keys).filter((candidate) => !keyless.includes(candidate));
  return { ...sale, billDiscountList: list };
}

const failureWords = (refusal: { message?: string; code?: string }): string => refusal.message || refusal.code || RELEASE_FAILED_MESSAGE;

export function createSaleEffectsExecutor(deps: SaleEffectsExecutorDeps): SaleEffectsExecutor {
  let refundInFlight = false;
  const releasing = new Set<string>();
  const fetching = new Set<string>();

  async function runReleasePlan(steps: ReleaseStep[], subject: Sale | null | undefined, summary: ExecutionSummary) {
    const directory = deps.directory();
    // the sale the partners know: the discarded one for a step that writes nothing back, else the sale as it stands
    const saleFor = (writesBack: boolean): Sale => (writesBack ? deps.currentSale() : (subject as Sale));
    let answered: Sale | undefined;
    const takenOff: BillDiscount[] = [];

    for (const step of steps) {
      if (step.kind === "refundPoints") {
        if (refundInFlight) {
          summary.skipped.push({ kind: "refundPoints", reason: "in-flight" });
          continue;
        }
        const provider = providerOfPointsLine(directory, step.entry);
        if (!provider?.mid) {
          deps.toast.show("error", REFUND_FAILED_MESSAGE);
          summary.skipped.push({ kind: "refundPoints", reason: "no-provider" });
          continue;
        }
        refundInFlight = true;
        try {
          const outcome = await deps.port.refundPoints({ sale: saleFor(step.writeBack), providerId: provider.mid, directory });
          summary.ran.push({ kind: "refundPoints", result: outcome.kind });
          if (outcome.kind === "refunded") {
            // a discarded sale is dropped: nothing to write back
            if (step.writeBack && outcome.sale) answered = outcome.sale;
            // v1 said so only for a bill change: the discard's refund had no callback, and its failure alone toasted
            if (step.writeBack) deps.toast.show("success", REFUND_DONE_MESSAGE);
          } else {
            // the backend still holds the points, so the line stays
            deps.toast.show("error", REFUND_FAILED_MESSAGE);
          }
        } catch {
          summary.ran.push({ kind: "refundPoints", result: "failed" });
          deps.toast.show("error", REFUND_FAILED_MESSAGE);
        } finally {
          refundInFlight = false;
        }
        continue;
      }

      const key = entryKey(step.entry) ?? "";
      if (key && releasing.has(key)) {
        summary.skipped.push({ kind: "releaseCoupon", reason: "in-flight" });
        continue;
      }
      if (key) releasing.add(key);
      try {
        const outcome = await deps.port.release({ entry: step.entry, sale: answered ?? saleFor(step.takeOff), directory });
        summary.ran.push({ kind: "releaseCoupon", result: outcome.kind });
        if (outcome.kind === "failed") deps.toast.show("error", failureWords(outcome));
        else if (outcome.kind === "skipped") {
          deps.toast.show("error", outcome.missing === "customerPhone" ? RELEASE_SKIPPED_PHONE_MESSAGE : RELEASE_SKIPPED_OTHER_MESSAGE);
        }
        // the coupon comes off even when its release failed (v1's DotPe effect stripped it regardless)
        if (step.takeOff) takenOff.push(step.entry);
      } catch {
        summary.ran.push({ kind: "releaseCoupon", result: "failed" });
        deps.toast.show("error", RELEASE_FAILED_MESSAGE);
        if (step.takeOff) takenOff.push(step.entry);
      } finally {
        if (key) releasing.delete(key);
      }
    }

    if (answered || takenOff.length > 0) {
      // one write: the refunded sale (else the sale as it stands now, after every await) minus what was taken off
      deps.write(takenOff.length > 0 ? takeOffEntries(answered ?? deps.currentSale(), takenOff) : (answered as Sale));
      summary.writes += 1;
    }
  }

  /**
   * v1's `ensureCustomerLoyaltyInfo`: with an active provider and a record not yet loaded for this customer,
   * ask for the customer's detailed record. The loader has no completion signal, so the in-flight guard is
   * released on the next tick, as v1's was: it collapses synchronous repeats, it does not span the request.
   */
  function runCustomerFetch({ fetchLoyaltyRecordFor: customer }: CustomerLoyaltyFetchPlan, summary: ExecutionSummary) {
    if (!deps.directory().active) {
      summary.skipped.push({ kind: "fetchLoyaltyRecord", reason: "no-provider" });
      return;
    }
    if (deps.isCustomerRecordLoaded(customer)) {
      summary.skipped.push({ kind: "fetchLoyaltyRecord", reason: "loaded" });
      return;
    }
    if (fetching.has(customer)) {
      summary.skipped.push({ kind: "fetchLoyaltyRecord", reason: "in-flight" });
      return;
    }
    fetching.add(customer);
    try {
      deps.loadCustomerRecord(customer);
      summary.ran.push({ kind: "fetchLoyaltyRecord", result: "requested" });
    } catch {
      summary.ran.push({ kind: "fetchLoyaltyRecord", result: "failed" });
    }
    setTimeout(() => fetching.delete(customer), 0);
  }

  return {
    run(previous, next, intent = {}) {
      const summary: ExecutionSummary = { ran: [], skipped: [], writes: 0 };
      const plans = runSaleEffects(previous, next, intent, PROMOTION_EFFECTS, { directory: deps.directory() });
      if (plans.length === 0) return Promise.resolve(summary);
      if (!deps.isOnline()) {
        plans.forEach(({ id, plan }) => {
          if (id === "promotionRelease") (plan as ReleaseStep[]).forEach((step) => summary.skipped.push({ kind: step.kind, reason: "offline" }));
          else summary.skipped.push({ kind: "fetchLoyaltyRecord", reason: "offline" });
        });
        return Promise.resolve(summary);
      }
      // a discarded sale is dropped, so what is released is the one the partners know: the previous (as `planDiscard` read it)
      const subject = previous ?? next;
      const running = plans.map(({ id, plan }) =>
        id === "promotionRelease" ? runReleasePlan(plan as ReleaseStep[], subject, summary) : Promise.resolve(runCustomerFetch(plan as CustomerLoyaltyFetchPlan, summary)),
      );
      return Promise.all(running).then(() => summary);
    },
  };
}
